import { ReactNode, type RefObject } from 'react';
import Link, { type LinkProps } from 'next/link';
import { useBasePath } from '../../../lib/use-base-path';
import styles from './Tabs.module.css';

// THE tab strip for the whole app — Figma "⚠️ Symphony DS" → 🟢 Tabs Group (node 1132:1578).
// Never hand-roll a tab strip; the geometry, the four states and the size ladder live in
// Tabs.module.css and are pinned by test/tabs-spec.test.ts.
//
// A tab is not a DS Button and never becomes one: a button performs an ACTION, a tab says WHICH VIEW
// you are looking at. That distinction is why this is its own primitive rather than a Button skin —
// it owns the selected state, the roles and the keyboard semantics that go with a selection control.
//
// **Two accessibility shapes, picked from the items — because the two are genuinely different
// controls.** A tab that only flips local state is a tab: the group is a `tablist` and each item
// reports `aria-selected`. A tab that navigates to its own route (`href`) is a navigation link: the
// group is a landmark `nav` and the current item reports `aria-current="page"`. Forcing routed links
// through `tablist` would announce "tab" for something that changes the URL and moves focus to a new
// document, which is a lie a screen-reader user pays for. Same pixels, honest semantics.
export type TabsSize = 'large' | 'medium' | 'small';

export interface TabItem<T extends string = string> {
  /** Identifies the tab; compared against `value` to find the selected one. */
  value: T;
  label: ReactNode;
  /** Figma State=Disabled — stays visible, cannot be selected. Use sparingly. In a routed group a
   *  disabled item is rendered WITHOUT its link (see below), so it is genuinely unreachable. */
  disabled?: boolean;
  /** Present ⇒ this tab IS a route, and the whole group renders as navigation links. */
  href?: LinkProps['href'];
  /** The browser-facing path for `href`. Required when `href` is a URL object; a string `href` gets
   *  the active mount prefix, exactly as the DS Button does. */
  linkAs?: string;
  /** Keep one history entry across tabs (a workspace is one screen, so switching must not push). */
  replace?: boolean;
  onClick?: () => void;
}

export interface TabsProps<T extends string = string> {
  items: readonly TabItem<T>[];
  /** The selected tab's `value`. Selection is the caller's state — the URL, usually. */
  value: T;
  /** Called with the picked tab. Not needed when every item navigates via `href`. */
  onChange?: (value: T) => void;
  /** Figma Size=L | M | S → 42 / 36 / 30. `large` steps down to `medium` in the app's narrow mode. */
  size?: TabsSize;
  /** The group's accessible name (`aria-label`) — a tab strip is a named control, not decoration. */
  label: string;
  className?: string;
  /** The group element, for a call site that has to scroll the strip into view. */
  containerRef?: RefObject<HTMLDivElement | null>;
}

export function Tabs<T extends string = string>({
  items,
  value,
  onChange,
  size = 'large',
  label,
  className,
  containerRef,
}: TabsProps<T>) {
  const basePath = useBasePath();
  const rootClass = [styles.root, className].filter(Boolean).join(' ');

  const tabClass = (item: TabItem<T>) =>
    [styles.tab, styles[`size-${size}`], item.value === value && styles.selected].filter(Boolean).join(' ');

  // A group is routed only when EVERY item is a route — one strip is one kind of control, so a
  // half-navigating tablist is not a shape to support.
  const routes = items.flatMap((item) => (item.href !== undefined ? [{ item, href: item.href }] : []));
  if (routes.length === items.length && items.length > 0) {
    return (
      <nav ref={containerRef} className={rootClass} aria-label={label}>
        {routes.map(({ item, href }) =>
          // A disabled routed tab DROPS its link rather than guarding it. `aria-disabled` does not
          // disable an anchor — it stays focusable, still runs `onClick` and still navigates — and
          // `preventDefault` + `tabIndex={-1}` would not close the hole either, because a real `<a
          // href>` opens on cmd-click, middle-click and "open in new tab" without running any
          // handler at all. That is the same call the Connectors WhatsApp CTA documents (see
          // orion-web/CLAUDE.md): drop the href, do not guard it. So the pill renders as inert text,
          // which is what "cannot be selected" has to mean if the prop is to be worth anything.
          item.disabled ? (
            <span key={item.value} aria-disabled="true" className={tabClass(item)}>
              {item.label}
            </span>
          ) : (
            <Link
              key={item.value}
              href={href}
              as={item.linkAs ?? (typeof href === 'string' ? `${basePath}${href}` : undefined)}
              replace={item.replace}
              onClick={item.onClick}
              aria-current={item.value === value ? 'page' : undefined}
              className={tabClass(item)}
            >
              {item.label}
            </Link>
          ),
        )}
      </nav>
    );
  }

  return (
    <div ref={containerRef} className={rootClass} role="tablist" aria-label={label}>
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          role="tab"
          aria-selected={item.value === value}
          disabled={item.disabled}
          className={tabClass(item)}
          onClick={() => {
            item.onClick?.();
            onChange?.(item.value);
          }}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}
